import { useCallback, useMemo, useRef } from 'react';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { logger } from '../modules/logger';
import { AppRoutePath, NOT_FOUND_CASE_ID } from '../config/constants';
import {
  dispatchExtensionEvent,
  transitionRouteEvent,
  closeWindowEvent,
} from '../domains/extension/extensionEventCreators';
import { ExtWindowName } from './useExtensionState';
import { useGetOpenTabsInfo } from './useOpenTabs';
import { useNavigate } from 'react-router-dom';
import { openPopup } from 'utils/openPopup';
import { trackWorklistAction } from 'domains/worklist/analytics/useWorklistAnalytics';
import { worklist, workspace } from 'modules/analytics/constants';
import { WithApolloContext } from 'modules/Apollo/types';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import analytics from 'modules/analytics';
import { useCaseSync } from './useCaseSync';
import { isAnyBlankRouteOpen } from 'utils/urls';
import { useUrlBuilder } from './useUrlBuilder';

export enum PatientJacketMode {
  Docked = 'docked',
  Undocked = 'undocked',
}

export type UseManagePatientJacketReturn = {
  managePatientJacket: (params?: {
    nextCaseSmid?: Nullable<string>;
    forceOpen?: boolean;
    trigger?: string;
  }) => void;
};

interface ManagePatientJacketParams {
  nextCaseSmid?: Nullable<string>;
  forceOpen?: boolean;
  trigger?: string;
}

export const useManagePatientJacket = ({
  context,
}: WithApolloContext): UseManagePatientJacketReturn => {
  const navigate = useNavigate();
  const { data } = useCurrentUser({ context });
  const me = data?.me;
  const shouldPatientJacketBeDocked = useMemo(
    () => me?.worklistSettings?.isPatientJacketDocked ?? true,
    [me]
  );
  const openPatientJacketOnRead = useMemo(
    () => me?.worklistSettings?.openPatientJacketOnRead ?? true,
    [me]
  );
  const syncCase = useCaseSync({ context });

  const buildViewerUrl = useUrlBuilder({ isViewer: true });
  const buildReporterUrl = useUrlBuilder({ isReporter: true });
  const buildAISandboxUrl = useUrlBuilder({ isAISandbox: true });
  const getOpenTabsInfo = useGetOpenTabsInfo();

  const [isClearPatientContextEnabled, isClearPatientContextEnabledLoading] = useFeatureFlagEnabled(
    FF.CLEAR_PATIENT_CONTEXT
  );

  const latestState = useRef({
    navigate,
    shouldPatientJacketBeDocked,
    openPatientJacketOnRead,
    syncCase,
    buildViewerUrl,
    buildReporterUrl,
    buildAISandboxUrl,
    isClearPatientContextEnabled,
    isClearPatientContextEnabledLoading,
    getOpenTabsInfo,
  });
  latestState.current = {
    navigate,
    shouldPatientJacketBeDocked,
    openPatientJacketOnRead,
    syncCase,
    buildViewerUrl,
    buildReporterUrl,
    buildAISandboxUrl,
    isClearPatientContextEnabled,
    isClearPatientContextEnabledLoading,
    getOpenTabsInfo,
  };

  const managePatientJacket = useCallback((params: ManagePatientJacketParams = {}) => {
    const { nextCaseSmid = null, forceOpen = false, trigger = '' } = params;

    const {
      navigate,
      shouldPatientJacketBeDocked,
      openPatientJacketOnRead,
      syncCase,
      buildViewerUrl,
      buildReporterUrl,
      buildAISandboxUrl,
      isClearPatientContextEnabled,
      isClearPatientContextEnabledLoading,
      getOpenTabsInfo,
    } = latestState.current;

    const {
      isAISandboxOpen,
      isReporterOpen,
      isViewer0Open,
      isViewer1Open,
      isJacketOpen,
      isPatientJacketDockedOpen,
      isPatientJacketUndockedOpen,
      tabs,
    } = getOpenTabsInfo();
    const isCurrentWindowWorklist = window.location.pathname.includes('worklist');

    const isBlankRouteOpen = isAnyBlankRouteOpen(tabs);

    const openDockedPatientJacket = (smid: string) => {
      trackWorklistAction(worklist.usr.openDockedPatientJacket, {
        isCurrentWindowWorklist,
        worklistItemSmid: smid,
      });

      if (isCurrentWindowWorklist) {
        navigate(AppRoutePath.PATIENT_JACKET.replace(':smid', smid));
      } else {
        dispatchExtensionEvent(
          transitionRouteEvent({
            target: ExtWindowName.Worklist,
            path: `${AppRoutePath.PATIENT_JACKET.replace(':smid', smid)}`,
          })
        );
      }
    };

    const clearPatientJacket = (clearTrigger: Nullable<string>) => {
      analytics.track(workspace.usr.clearCase, {
        window: ExtWindowName.Patient_Jacket,
        trigger: clearTrigger,
      });
      dispatchExtensionEvent(
        transitionRouteEvent({
          target: ExtWindowName.Patient_Jacket,
          path: AppRoutePath.PATIENT_JACKET_BLANK,
        })
      );
    };

    const closePatientJacket = (closeTrigger: Nullable<string>) => {
      trackWorklistAction(worklist.usr.closePatientJacket, {
        isCurrentWindowWorklist,
        mode: isPatientJacketDockedOpen ? PatientJacketMode.Docked : PatientJacketMode.Undocked,
        trigger: closeTrigger,
      });

      if (isCurrentWindowWorklist) {
        navigate(AppRoutePath.WORKLIST);
      } else if (isPatientJacketDockedOpen) {
        dispatchExtensionEvent(
          transitionRouteEvent({
            target: ExtWindowName.Worklist,
            path: AppRoutePath.WORKLIST,
          })
        );
      } else {
        dispatchExtensionEvent(
          closeWindowEvent({
            target: ExtWindowName.Patient_Jacket,
          })
        );
      }
    };

    // Handle when there is no next case. When this happens, we will do one of the following:
    // 1) Close the patient jacket, if the jacket is open and docked
    // 2) Clear the patient jacket, if the jacket is open and undocked
    // 3) Open a blank patient jacket, if the jacket is closed and undocked
    if (!nextCaseSmid || nextCaseSmid === NOT_FOUND_CASE_ID) {
      if (isClearPatientContextEnabled && !isClearPatientContextEnabledLoading) {
        if (!shouldPatientJacketBeDocked && !isJacketOpen && forceOpen) {
          logger.info('[useManagePatientJacket] opening blank patient jacket', {
            nextCaseSmid,
            openPatientJacketOnRead,
            isPatientJacketOpen: isJacketOpen,
          });
          analytics.track(workspace.usr.openBlankRoute, {
            window: ExtWindowName.Patient_Jacket,
            trigger,
          });
          openPopup(AppRoutePath.PATIENT_JACKET_BLANK);
          return;
        }

        if (isPatientJacketUndockedOpen) {
          logger.info('[useManagePatientJacket] sending clearPatientJacket event', {
            nextCaseSmid,
            openPatientJacketOnRead,
            isPatientJacketOpen: isJacketOpen,
          });
          clearPatientJacket(trigger);
          return;
        }
      }

      logger.info('[useManagePatientJacket] sending closePatientJacket event', {
        nextCaseSmid,
        openPatientJacketOnRead,
        isPatientJacketOpen: isJacketOpen,
      });
      closePatientJacket(trigger);
      return;
    }

    if (isClearPatientContextEnabled) {
      logger.info('[useManagePatientJacket] syncing next case', {
        nextCaseSmid,
        openPatientJacketOnRead,
        isPatientJacketOpen: isJacketOpen,
        tabs,
      });

      if (isBlankRouteOpen) {
        logger.info('[useManagePatientJacket] opening next case', {
          nextCaseSmid,
          openPatientJacketOnRead,
          isPatientJacketOpen: isJacketOpen,
          tabs,
        });

        if (isViewer0Open) {
          dispatchExtensionEvent(
            transitionRouteEvent({
              target: ExtWindowName.Viewer0,
              path: buildViewerUrl({ caseSmid: nextCaseSmid, viewerId: '0' }) ?? '',
            })
          );
        }

        if (isViewer1Open) {
          dispatchExtensionEvent(
            transitionRouteEvent({
              target: ExtWindowName.Viewer1,
              path: buildViewerUrl({ caseSmid: nextCaseSmid, viewerId: '1' }) ?? '',
            })
          );
        }

        if (isReporterOpen) {
          dispatchExtensionEvent(
            transitionRouteEvent({
              target: ExtWindowName.Reporter,
              path: buildReporterUrl({ caseSmid: nextCaseSmid }) ?? '',
            })
          );
        }

        if (isAISandboxOpen) {
          dispatchExtensionEvent(
            transitionRouteEvent({
              target: ExtWindowName.AISandbox,
              path: buildAISandboxUrl({ caseSmid: nextCaseSmid }) ?? '',
            })
          );
        }
      }

      syncCase(nextCaseSmid, undefined, false);
    }

    // Handle when there is a next case, and the jacket is already open. We will transition to the next case in the same jacket
    if (isJacketOpen) {
      logger.info('[useManagePatientJacket] sending nextPatientJacket event', {
        nextCaseSmid,
        openPatientJacketOnRead,
        isPatientJacketOpen: isJacketOpen,
      });
      trackWorklistAction(worklist.usr.transitionPatientJacket, {
        forceOpen,
        worklistItemSmid: nextCaseSmid,
        mode: isPatientJacketUndockedOpen ? PatientJacketMode.Undocked : PatientJacketMode.Docked,
      });

      if (isPatientJacketUndockedOpen) {
        dispatchExtensionEvent(
          transitionRouteEvent({
            target: ExtWindowName.Patient_Jacket,
            path: `${AppRoutePath.PATIENT_JACKET_UNDOCKED.replace(':smid', nextCaseSmid)}`,
          })
        );
      } else {
        openDockedPatientJacket(nextCaseSmid);
      }

      return;
    }

    // Handle when there is a next case, and the jacket is currently closed.
    if (forceOpen || openPatientJacketOnRead) {
      logger.info('[useManagePatientJacket] sending openPatientJacket event', {
        nextCaseSmid,
        openPatientJacketOnRead,
        isPatientJacketOpen: isJacketOpen,
      });

      if (shouldPatientJacketBeDocked) {
        openDockedPatientJacket(nextCaseSmid);
      } else {
        trackWorklistAction(worklist.usr.openUndockedPatientJacket, {
          worklistItemSmid: nextCaseSmid,
        });
        const url = `${AppRoutePath.PATIENT_JACKET_UNDOCKED.replace(':smid', nextCaseSmid)}`;
        openPopup(url);
      }
      return;
    }
  }, []);

  return { managePatientJacket };
};
